'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Server, 
  Wifi, 
  Router, 
  Shield, 
  Maximize2, 
  Minimize2,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Layers
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface DeviceNode {
  id: string;
  name: string;
  type: 'core' | 'distribution' | 'access' | 'firewall' | 'wireless';
  status: 'online' | 'offline' | 'warning';
  ip: string;
  x: number;
  y: number;
  connections: string[];
}

const devices: DeviceNode[] = [
  { id: 'fw1', name: '主防火墙', type: 'firewall', status: 'online', ip: '192.168.1.1', x: 400, y: 50, connections: ['core1', 'core2'] },
  { id: 'core1', name: '核心交换机-A', type: 'core', status: 'online', ip: '192.168.1.2', x: 250, y: 150, connections: ['dist1', 'dist2'] },
  { id: 'core2', name: '核心交换机-B', type: 'core', status: 'online', ip: '192.168.1.3', x: 550, y: 150, connections: ['dist1', 'dist2'] },
  { id: 'dist1', name: '汇聚交换机-1', type: 'distribution', status: 'online', ip: '192.168.2.1', x: 200, y: 280, connections: ['access1', 'access2'] },
  { id: 'dist2', name: '汇聚交换机-2', type: 'distribution', status: 'warning', ip: '192.168.2.2', x: 500, y: 280, connections: ['access3', 'access4'] },
  { id: 'access1', name: '接入层-办公区A', type: 'access', status: 'online', ip: '192.168.3.1', x: 100, y: 400, connections: [] },
  { id: 'access2', name: '接入层-办公区B', type: 'access', status: 'online', ip: '192.168.3.2', x: 250, y: 400, connections: [] },
  { id: 'access3', name: '接入层-服务器区', type: 'access', status: 'online', ip: '192.168.3.3', x: 450, y: 400, connections: [] },
  { id: 'access4', name: '接入层-会议室', type: 'access', status: 'offline', ip: '192.168.3.4', x: 600, y: 400, connections: [] },
  { id: 'ap1', name: '无线AP-大厅', type: 'wireless', status: 'online', ip: '192.168.4.1', x: 350, y: 350, connections: ['dist1', 'dist2'] },
];

const deviceIcons = {
  core: Server,
  distribution: Router,
  access: Router,
  firewall: Shield,
  wireless: Wifi,
};

const deviceColors = {
  core: { bg: 'bg-[var(--primary)]', border: 'border-[var(--primary)]' },
  distribution: { bg: 'bg-[var(--info)]', border: 'border-[var(--info)]' },
  access: { bg: 'bg-[var(--success)]', border: 'border-[var(--success)]' },
  firewall: { bg: 'bg-[var(--warning)]', border: 'border-[var(--warning)]' },
  wireless: { bg: 'bg-purple-500', border: 'border-purple-500' },
};

const statusColors = {
  online: 'bg-[var(--success)]',
  offline: 'bg-[var(--destructive)]',
  warning: 'bg-[var(--warning)]',
};

export default function NetworkTopology() {
  const [zoom, setZoom] = useState(1);
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.2, 2));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.2, 0.5));
  const handleReset = () => setZoom(1);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.2 }}
      className={cn(
        "bg-white rounded-xl border border-[var(--border)] overflow-hidden",
        isFullscreen ? "fixed inset-4 z-50" : ""
      )}
    >
      {/* 头部 */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[var(--primary)]/10 flex items-center justify-center">
            <Layers className="w-5 h-5 text-[var(--primary)]" />
          </div>
          <div>
            <h3 className="font-semibold text-[var(--foreground)]">网络拓扑图</h3>
            <p className="text-xs text-[var(--muted-foreground)]">实时显示网络设备连接状态</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* 缩放控制 */}
          <div className="flex items-center bg-[var(--secondary)] rounded-lg p-1">
            <button 
              onClick={handleZoomOut}
              className="p-1.5 rounded hover:bg-white text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="px-2 text-sm text-[var(--muted-foreground)] min-w-[3rem] text-center">
              {Math.round(zoom * 100)}%
            </span>
            <button 
              onClick={handleZoomIn}
              className="p-1.5 rounded hover:bg-white text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button 
              onClick={handleReset}
              className="p-1.5 rounded hover:bg-white text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors ml-1"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>

          {/* 全屏切换 */}
          <button 
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-2 rounded-lg border border-[var(--border)] text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:border-[var(--foreground)]/20 transition-colors"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* 拓扑画布 */}
      <div className="relative h-[400px] bg-gradient-to-br from-[var(--secondary)] to-white overflow-hidden">
        {/* 背景网格 */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `
              linear-gradient(to right, var(--border) 1px, transparent 1px),
              linear-gradient(to bottom, var(--border) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px'
          }}
        />

        {/* SVG 连线 */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none">
          <defs>
            <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="#E2E8F0" />
            </marker>
          </defs>
          {devices.map(device => 
            device.connections.map(targetId => {
              const target = devices.find(d => d.id === targetId);
              if (!target) return null;
              return (
                <motion.line
                  key={`${device.id}-${targetId}`}
                  x1={device.x}
                  y1={device.y}
                  x2={target.x}
                  y2={target.y}
                  stroke="#E2E8F0"
                  strokeWidth="2"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.5 }}
                />
              );
            })
          )}
          
          {/* 活跃链路动画 */}
          {devices.map(device => 
            device.connections.map(targetId => {
              const target = devices.find(d => d.id === targetId);
              if (!target || device.status !== 'online') return null;
              return (
                <line
                  key={`active-${device.id}-${targetId}`}
                  x1={device.x}
                  y1={device.y}
                  x2={target.x}
                  y2={target.y}
                  stroke="var(--primary)"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                  className="animate-flow opacity-50"
                />
              );
            })
          )}
        </svg>

        {/* 设备节点 */}
        <div 
          className="absolute inset-0"
          style={{ 
            transform: `scale(${zoom})`,
            transformOrigin: 'center center'
          }}
        >
          {devices.map((device) => {
            const Icon = deviceIcons[device.type];
            const colors = deviceColors[device.type];
            const isSelected = selectedDevice === device.id;

            return (
              <motion.div
                key={device.id}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3, delay: devices.indexOf(device) * 0.05 }}
                className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                style={{ left: device.x, top: device.y }}
                onClick={() => setSelectedDevice(isSelected ? null : device.id)}
              >
                <div className={cn(
                  "relative flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all duration-200 bg-white",
                  isSelected ? `border-[var(--primary)] shadow-lg ring-2 ring-[var(--primary)]/20` : `border-[var(--border)] hover:border-[var(--primary)]/50 hover:shadow-md`
                )}>
                  {/* 设备图标 */}
                  <div className={cn(
                    "w-12 h-12 rounded-xl flex items-center justify-center",
                    colors.bg.replace('bg-', 'bg-opacity-10 ')
                  )}>
                    <Icon className={cn("w-6 h-6", colors.bg.replace('bg-', 'text-'))} />
                  </div>
                  
                  {/* 设备名称 */}
                  <div className="text-center">
                    <p className="text-xs font-medium text-[var(--foreground)] whitespace-nowrap">{device.name}</p>
                    <p className="text-[10px] text-[var(--muted-foreground)] font-mono">{device.ip}</p>
                  </div>
                  
                  {/* 状态指示灯 */}
                  <div className="absolute -top-1 -right-1">
                    <div className={cn(
                      "w-3 h-3 rounded-full border-2 border-white",
                      statusColors[device.status],
                      device.status === 'online' && "animate-pulse-slow"
                    )} />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* 图例 */}
        <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg border border-[var(--border)] p-3">
          <p className="text-xs font-medium text-[var(--foreground)] mb-2">图例</p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[var(--success)]" />
              <span className="text-xs text-[var(--muted-foreground)]">在线</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[var(--warning)]" />
              <span className="text-xs text-[var(--muted-foreground)]">告警</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[var(--destructive)]" />
              <span className="text-xs text-[var(--muted-foreground)]">离线</span>
            </div>
          </div>
        </div>

        {/* 选中设备详情 */}
        {selectedDevice && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="absolute top-4 right-4 w-64 bg-white rounded-xl border border-[var(--border)] shadow-lg p-4"
          >
            {(() => {
              const device = devices.find(d => d.id === selectedDevice);
              if (!device) return null;
              const Icon = deviceIcons[device.type];
              return (
                <>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center",
                      deviceColors[device.type].bg.replace('bg-', 'bg-opacity-10 ')
                    )}>
                      <Icon className={cn("w-5 h-5", deviceColors[device.type].bg.replace('bg-', 'text-'))} />
                    </div>
                    <div>
                      <p className="font-medium text-[var(--foreground)]">{device.name}</p>
                      <p className="text-xs text-[var(--muted-foreground)]">{device.ip}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-[var(--muted-foreground)]">状态</span>
                      <span className={cn(
                        "font-medium",
                        device.status === 'online' && "text-[var(--success)]",
                        device.status === 'warning' && "text-[var(--warning)]",
                        device.status === 'offline' && "text-[var(--destructive)]"
                      )}>
                        {device.status === 'online' ? '在线' : device.status === 'warning' ? '告警' : '离线'}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[var(--muted-foreground)]">类型</span>
                      <span className="text-[var(--foreground)]">
                        {device.type === 'core' ? '核心层' : 
                         device.type === 'distribution' ? '汇聚层' : 
                         device.type === 'access' ? '接入层' : 
                         device.type === 'firewall' ? '防火墙' : '无线设备'}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[var(--muted-foreground)]">连接数</span>
                      <span className="text-[var(--foreground)]">{device.connections.length}</span>
                    </div>
                  </div>
                  <button 
                    onClick={() => setSelectedDevice(null)}
                    className="mt-3 w-full py-2 text-xs text-[var(--primary)] hover:bg-[var(--primary)]/5 rounded-lg transition-colors"
                  >
                    查看详情
                  </button>
                </>
              );
            })()}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
